/** Plan 03-12 — NotificationHubService.
 *
 *  Wraps @microsoft/signalr HubConnection lifecycle for the SPA's global notifications hub
 *  (separate from Phase 2's per-submission validation hub). User-scoped fan-out: the SignalR
 *  Service binds the connection to UserId={sub} via the negotiate endpoint, so pushes targeted
 *  at UserId reach all of the user's open tabs (the SignalR Service multiplexes; the SPA does
 *  NOT need to track its own group membership).
 *
 *  - Negotiate URL: ${apiBase}/api/hubs/notifications/negotiate
 *      (Plan 03-12 ships the corresponding NegotiateNotificationsFunction in
 *      nepf-functions/src/NepfPortal.Functions.Notifications/ that returns
 *      {url, accessToken} via the SignalRConnectionInfoInput binding with
 *      HubName="notifications" + UserId="{headers.x-user-sub}".)
 *  - accessTokenFactory pulls the JWT from keycloak-angular's KeycloakService (mirrors
 *      Phase 2 02-15 ValidationHubService).
 *  - Auto-reconnect: [0, 2000, 5000, 10000, 30000] — slightly wider than Phase 2's
 *      validation hub because notifications are long-lived (whole-session) and can tolerate
 *      a longer final retry before falling back to fresh page load.
 *  - 'notification' event handler emits NotificationPush onto a Subject<NotificationPush>
 *      after passing per-tab dedup (Pitfall 5: same correlationId arriving on multiple tabs).
 *
 *  CONNECTION STATE (signal): 'disconnected' | 'connecting' | 'connected' | 'reconnecting'
 *  The signal lets components show a connection-status pill in the header (Plan 03-15 wiring).
 */

import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  LogLevel,
} from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';
import Keycloak from 'keycloak-js';
import { environment } from '../../../environments/environment';
import type { NotificationPush } from '../models/notification-push';

/**
 * Reconnect backoff in ms — exponential-ish steps with a final 30 s cap. Picked wider than
 * Phase 2's [0, 2000, 5000, 10000] because the notifications hub is whole-session and a longer
 * 5th retry is acceptable before the user gets a "reconnecting…" UI signal that they may want
 * to refresh.
 */
export const RECONNECT_BACKOFF_MS = [0, 2_000, 5_000, 10_000, 30_000];

/** Max correlationIds retained in the per-tab dedup Set before LRU eviction. */
export const SEEN_CORRELATION_ID_CAPACITY = 200;

/** SignalR connection state machine surface for templates / status pills. */
export type NotificationHubConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting';

@Injectable({ providedIn: 'root' })
export class NotificationHubService implements OnDestroy {
  // keycloak-angular v21 -- inject the underlying Keycloak instance directly.
  private readonly kc = inject(Keycloak);
  private connection: HubConnection | null = null;
  private readonly _push$ = new Subject<NotificationPush>();

  /**
   * Per-tab bounded Set of correlationIds already delivered. Insertion order = LRU; the
   * oldest entry is evicted when capacity is hit (Pitfall 5 mitigation).
   */
  private readonly seenCorrelationIds = new Set<string>();

  /** Connection state for templates (read-only signal). */
  readonly connectionState = signal<NotificationHubConnectionState>('disconnected');

  /** Stream of de-duped notification pushes; consumed by NotificationToastService (Plan 03-15). */
  readonly push$: Observable<NotificationPush> = this._push$.asObservable();

  /**
   * Open the global notifications connection. Idempotent: if already open, returns immediately.
   * Designed to be invoked once at app startup (provideAppInitializer wiring in app.config.ts)
   * AFTER Keycloak finishes its own bootstrap.
   */
  async connect(): Promise<void> {
    if (this.connection) {
      // Already started; nothing to do.
      return;
    }

    const sub = await this.resolveUserSub();
    if (!sub) {
      // No identity yet (e.g., Keycloak not authenticated in Dev or check-sso unresolved).
      // Skip connect; the SPA can call connect() again after login (Plan 03-15 component will
      // re-invoke after KeycloakService.login()).
      return;
    }

    // SignalR's HubConnectionBuilder.withUrl(X).start() POSTs to `X/negotiate?...`,
    // so X must NOT include the trailing `/negotiate`. The serverless Azure Function
    // (NegotiateNotificationsFunction) is mounted at /api/hubs/notifications/negotiate
    // on the func-nepf-notif-{env} Function App -- a SEPARATE host from the ASP.NET
    // API because the SignalRConnectionInfoInput binding only runs in a Functions
    // host. Using environment.apiBase here would 404 because the API doesn't expose
    // that route. Configure CORS on the Function App to allow the SPA origin.
    const hubUrl = `${environment.functionsBase}/api/hubs/notifications`;

    const conn = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => this.resolveAccessToken(),
        // x-user-sub header is what the NegotiateNotificationsFunction's SignalRConnectionInfoInput
        // binds UserId from. Phase 6 SEC will tighten this to a server-side JWT-validated sub
        // (so the header trust assumption goes away); for now this matches Phase 2 02-15 pattern.
        headers: { 'x-user-sub': sub },
      })
      .withAutomaticReconnect(RECONNECT_BACKOFF_MS)
      .configureLogging(LogLevel.Warning)
      .build();

    conn.onreconnecting(() => this.connectionState.set('reconnecting'));
    conn.onreconnected(() => this.connectionState.set('connected'));
    conn.onclose(() => this.connectionState.set('disconnected'));

    conn.on('notification', (payload: NotificationPush) => this.onNotification(payload));

    this.connection = conn;
    this.connectionState.set('connecting');
    try {
      await conn.start();
      this.connectionState.set('connected');
    } catch {
      // Failed initial connect; reset state but don't crash the app initializer.
      this.connection = null;
      this.connectionState.set('disconnected');
    }
  }

  /** Tear down the connection. Idempotent. */
  async disconnect(): Promise<void> {
    if (this.connection) {
      const c = this.connection;
      this.connection = null;
      try {
        await c.stop();
      } catch {
        // Stop errors are not actionable; swallow per SignalR convention.
      }
      this.connectionState.set('disconnected');
    }
  }

  ngOnDestroy(): void {
    void this.disconnect();
  }

  /**
   * Per-tab dedup + emit. SignalR Service routes a single push to all of a user's tabs; without
   * dedup, every tab would emit (and Plan 03-15 would render duplicate toasts). The bounded Set
   * caps memory growth on long-lived sessions where a user receives thousands of events.
   *
   * NOTE: Two tabs of the same user STILL each see one toast — that's the desired UX (the user
   * IS in two contexts). The dedup here protects against the same SignalR push being processed
   * twice on a SINGLE tab (unusual but possible during reconnect storms).
   */
  private onNotification(payload: NotificationPush): void {
    if (!payload || typeof payload.correlationId !== 'string' || payload.correlationId.length === 0) {
      // Defensive: producer always sets correlationId, but a malformed push shouldn't crash the tab.
      return;
    }
    if (this.seenCorrelationIds.has(payload.correlationId)) {
      // Already delivered on this tab; skip.
      return;
    }
    if (this.seenCorrelationIds.size >= SEEN_CORRELATION_ID_CAPACITY) {
      // Bounded LRU eviction — Set preserves insertion order in JS, so the iterator's first
      // entry is the oldest. Removing one keeps the working-set steady.
      const oldest = this.seenCorrelationIds.values().next().value;
      if (oldest !== undefined) {
        this.seenCorrelationIds.delete(oldest);
      }
    }
    this.seenCorrelationIds.add(payload.correlationId);
    this._push$.next(payload);
  }

  /**
   * Resolve the JWT for SignalR's accessTokenFactory. keycloak-angular v21 exposes
   * the access token via the injected Keycloak instance's .token property; updateToken()
   * refreshes proactively (30s minimum validity) so we don't hand SignalR an expired token.
   */
  private async resolveAccessToken(): Promise<string> {
    try {
      // Refresh if token expires within 30s; safe no-op if it's still valid.
      await this.kc.updateToken(30);
      return this.kc.token ?? '';
    } catch {
      return this.kc.token ?? '';
    }
  }

  /**
   * Resolve the authenticated user's sub claim. Used to populate the x-user-sub header
   * which the negotiate Function's SignalRConnectionInfoInput binds to UserId.
   * Prefers tokenParsed.sub (cheap, in-memory); falls back to loadUserProfile.
   */
  private async resolveUserSub(): Promise<string> {
    const claimSub = this.kc.tokenParsed?.['sub'];
    if (typeof claimSub === 'string' && claimSub.length > 0) {
      return claimSub;
    }
    try {
      const profile = await this.kc.loadUserProfile();
      const sub = (profile as { id?: string }).id;
      if (sub && sub.length > 0) {
        return sub;
      }
    } catch {
      // Best-effort only.
    }
    return '';
  }
}
